import { useTranslation } from "react-i18next";
import {
    DAY_END_HOUR,
    DAY_START_HOUR,
    bandPosition,
    groupByWeekday,
} from "../../domain/weeklyHours";
import { WeeklyWindow } from "../../domain/types";

/**
 * The therapist's declared working hours.
 *
 * Read-only by construction, and deliberately not `scheduler/SlotGrid`, which
 * renders bookable slots. Nothing here is clickable, because nothing here is
 * an offer of a specific time — these are the hours the therapist works, not
 * the hours they happen to be free.
 */
export default function WeeklyHours({
    windows,
    timezone,
}: {
    windows: WeeklyWindow[];
    timezone: string | null;
}) {
    const { t } = useTranslation();
    const rows = groupByWeekday(windows);

    if (rows.length === 0) return null;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
                {rows.map((row) => (
                    <div key={row.dayOfWeek} className="grid grid-cols-[3rem_minmax(0,1fr)] items-center gap-3">
                        <span className="text-theme-xs font-medium text-content-tertiary">
                            {t(`publicDirectory.weekday.${row.dayKey}`)}
                        </span>
                        <div className="relative h-7 overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
                            {row.windows.map((window) => {
                                const { left, width } = bandPosition(window);
                                return (
                                    <span
                                        key={`${window.start_time}-${window.end_time}`}
                                        className="absolute inset-y-0 flex items-center rounded-lg bg-brand-500 pl-2 pr-2 text-theme-xs font-medium text-white whitespace-nowrap tabular-nums"
                                        style={{ left: `${left}%`, width: `${width}%` }}
                                    >
                                        {window.start_time}–{window.end_time}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-[3rem_minmax(0,1fr)] gap-3 text-theme-xs text-content-tertiary tabular-nums" aria-hidden="true">
                <span />
                <span className="flex justify-between">
                    <span>{DAY_START_HOUR}:00</span>
                    <span>{Math.round((DAY_START_HOUR + DAY_END_HOUR) / 2)}:00</span>
                    <span>{DAY_END_HOUR}:00</span>
                </span>
            </div>

            {timezone && (
                <p className="text-theme-xs text-content-tertiary">
                    {t("publicDirectory.timezoneNote", { timezone })}
                </p>
            )}
        </div>
    );
}
